import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Alert, Button, ModalBody, ModalFooter } from 'reactstrap';

import { UploadResult } from './types';

interface ResultsStepProps {
  uploadResult: UploadResult | null;
  onClose: () => void;
}

const ResultsStep: FC<ResultsStepProps> = ({ uploadResult, onClose }) => {
  const { formatMessage } = useIntl();

  const reasonLabels: Record<string, string> = {
    not_in_organization: formatMessage({
      id: 'app.custom_campaign_data.results.reason.not_in_org',
      defaultMessage: 'Not found in organization',
    }),
    not_adminable: formatMessage({
      id: 'app.custom_campaign_data.results.reason.not_adminable',
      defaultMessage: 'Not in your admin scope',
    }),
  };

  const skippedGroups = uploadResult
    ? Object.entries(
        uploadResult.skipped_records.reduce<Record<string, string[]>>(
          (acc, record) => {
            (acc[record.reason] ??= []).push(record.identifier);
            return acc;
          },
          {}
        )
      )
    : [];

  return (
    <>
      <ModalBody>
        {uploadResult && (
          <>
            <Alert color="success">
              <FormattedMessage
                id="app.custom_campaign_data.results.success"
                defaultMessage="{count} records uploaded successfully."
                values={{ count: uploadResult.record_count }}
              />
            </Alert>
            {uploadResult.new_columns.length > 0 && (
              <p>
                <strong>
                  <FormattedMessage
                    id="app.custom_campaign_data.results.new_columns"
                    defaultMessage="New columns:"
                  />
                </strong>{' '}
                {uploadResult.new_columns.map((c) => c.label).join(', ')}
              </p>
            )}
            {uploadResult.overwritten_columns.length > 0 && (
              <p>
                <strong>
                  <FormattedMessage
                    id="app.custom_campaign_data.results.overwritten_columns"
                    defaultMessage="Updated columns:"
                  />
                </strong>{' '}
                {uploadResult.overwritten_columns
                  .map((c) => c.label)
                  .join(', ')}
              </p>
            )}
            {skippedGroups.length > 0 && (
              <Alert color="warning">
                <FormattedMessage
                  id="app.custom_campaign_data.results.skipped"
                  defaultMessage="{count} records were skipped:"
                  values={{ count: uploadResult.skipped_records.length }}
                />
                {skippedGroups.map(([reason, identifiers]) => (
                  <div key={reason} className="mt-2">
                    <strong>
                      {`${reasonLabels[reason] ?? reason} (${
                        identifiers.length
                      })`}
                    </strong>
                    <ul className="mb-0 mt-1">
                      {identifiers.slice(0, 10).map((id, i) => (
                        <li key={i}>{id}</li>
                      ))}
                      {identifiers.length > 10 && (
                        <li>
                          <FormattedMessage
                            id="app.custom_campaign_data.results.and_more"
                            defaultMessage="...and {count} more"
                            values={{ count: identifiers.length - 10 }}
                          />
                        </li>
                      )}
                    </ul>
                  </div>
                ))}
              </Alert>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={onClose}>
          <FormattedMessage
            id="app.custom_campaign_data.done"
            defaultMessage="Done"
          />
        </Button>
      </ModalFooter>
    </>
  );
};

export default ResultsStep;
